import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomerInvoicesSection } from '@/components/customers/CustomerInvoicesSection';
import { CustomerAgreementsSection } from '@/components/customers/CustomerAgreementsSection';
import CustomerInfoCard from '@/components/customers/CustomerInfoCard';
import CustomerProjectsSection from '@/components/customers/CustomerProjectsSection';
import CustomerHero from '@/components/customers/CustomerHero';
import CustomerContactsSection from '@/components/customers/CustomerContactsSection';
import CustomerLastContactCard from '@/components/customers/CustomerLastContactCard';
import CustomerOpenOffersSection from '@/components/customers/CustomerOpenOffersSection';
import CompanyInfoSection from '@/components/customers/CompanyInfoSection';
import MonitoringSection from '@/components/customers/MonitoringSection';
import CustomerSignalFeed from '@/components/customers/CustomerSignalFeed';
import SendEmailDialog from '@/components/customers/SendEmailDialog';
import CustomerNextStepsSection from '@/components/customers/CustomerNextStepsSection';
import MeetingsSection from '@/components/customers/MeetingsSection';
import { useCustomerDetail } from '@/hooks/use-customer-detail';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import type { RecurringAgreementWithCustomer } from '@/hooks/use-recurring-agreements';

/**
 * Kundekort v3 (19.10): hero med logo, faktagrid og GEEK-stripe, så to kolonner
 * med ett kortspråk (SectionCard). Hovedkolonnen er det vi gjør med kunden
 * (neste steg, tilbud, avtaler, prosjekter), sidekolonnen er konteksten
 * (signaler, overvåkning, kontakter, siste kontakt, møter, bedriften). Tomme
 * seksjoner er én linje. Én kolonne på mobil.
 */
export default function CustomerDetailPage() {
  const { id } = useParams();
  const { data, isLoading, error } = useCustomerDetail(id);
  // Flagget gater innholdet, ikke ruten: /kunde/:id er ugatet i App.tsx.
  const crm = useFeatureFlag('feature_crm_kundekort');
  const signaler = useFeatureFlag('feature_crm_signaler');
  const koPipe = useFeatureFlag('feature_crm_ko_pipe');
  const leadsMoter = useFeatureFlag('feature_crm_leads_moter');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailContactId, setEmailContactId] = useState<string | null>(null);
  const {
    customer,
    projects: customerProjects = [],
    invoices = [],
    agreements = [],
  } = data || { customer: null, projects: [], invoices: [], agreements: [] };

  if (isLoading) {
    return <div className="page-container"><p>Laster kunde...</p></div>;
  }

  if (error) {
    return <div className="page-container"><p className="text-destructive">Feil ved lasting av kundedetaljer: {error.message}</p></div>;
  }

  if (!customer) {
    return <div className="page-container"><p>Kunde ikke funnet.</p></div>;
  }

  const openEmail = (contactId: string | null) => {
    setEmailContactId(contactId);
    setEmailOpen(true);
  };

  const agreementsSection = (
    <CustomerAgreementsSection
      customerId={customer.id}
      agreements={agreements as RecurringAgreementWithCustomer[]}
    />
  );

  if (!crm) {
    return (
      <div className="page-container animate-fade-in space-y-6">
        <h1 className="page-title">{customer.name}</h1>
        <CustomerInfoCard customer={customer} />
        <CustomerProjectsSection customerId={customer.id} projects={customerProjects} invoices={invoices} />
        {agreementsSection}
        <CustomerInvoicesSection invoices={invoices} />
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <CustomerHero customer={customer} projects={customerProjects} onSendEmail={openEmail} />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-5">
          {koPipe && <CustomerNextStepsSection customerId={customer.id} />}
          <CustomerOpenOffersSection customerId={customer.id} customerName={customer.name} />
          {agreementsSection}
          <CustomerProjectsSection customerId={customer.id} projects={customerProjects} invoices={invoices} />
        </div>

        <aside className="min-w-0 space-y-4" aria-label="Kontekst om kunden">
          {signaler && <CustomerSignalFeed customerId={customer.id} />}
          {signaler && <MonitoringSection customerId={customer.id} />}
          <CustomerContactsSection customerId={customer.id} />
          <CustomerLastContactCard customerId={customer.id} onSendEmail={openEmail} />
          {leadsMoter && <MeetingsSection customerId={customer.id} customerName={customer.name} />}
          {signaler && (
            <CompanyInfoSection
              customerId={customer.id}
              organizationNumber={customer.organization_number}
              isCompany={customer.is_company !== false}
              compact
            />
          )}
        </aside>
      </div>

      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        customerId={customer.id}
        defaultContactId={emailContactId}
      />
    </div>
  );
}
